import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ApiError, FormatToolbar, inboxApi, isAuthentication, TEMPLATE_LANGUAGES, TEMPLATE_LIMITS, templateToDraft,
  type TemplateButtonType, type TemplateDraft, type TemplateDraftButton,
} from '@wa/shared';
import { PhonePreview, s, shapeOfDraft } from '@/templates/parts';

/**
 * The template editor, in the reference's four steps: Basic Info, Content,
 * Buttons, Variables — with the phone beside it the whole way, because whether
 * a template reads well is not legible as a form.
 *
 * Steps rather than one long page for a reason the reference has right: Meta
 * rejects at review, hours later, and the things it rejects for are spread
 * across the form. A step can refuse to advance until its own part is sound,
 * which is the earliest a mistake can be caught.
 *
 * Values are numbered — {{1}}, {{2}} — because that is what this platform sends
 * and maps to contact fields at send time (F23). The chips above the body insert
 * the next number, so nobody types {{3}} before {{2}} and finds out at review.
 */
const BLANK: TemplateDraft = {
  name: '', language: 'en', category: 'MARKETING', headerFormat: 'NONE', body: '', bodyExamples: [], buttons: [],
};

const CATEGORIES = [
  { value: 'MARKETING', label: 'Marketing', hint: 'Send promotions or information about your products, services or business.', icon: <IconMegaphone /> },
  { value: 'UTILITY', label: 'Utility', hint: 'Send messages about an existing order or account.', icon: <IconBell /> },
  { value: 'AUTHENTICATION', label: 'Authentication', hint: 'Send verification code for transaction confirmation, account login or identity verification.', icon: <IconShield /> },
] as const;

const HEADERS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'] as const;
const ACCEPT: Record<string, string> = { IMAGE: 'image/jpeg,image/png', VIDEO: 'video/mp4', DOCUMENT: 'application/pdf' };
const OPT_OUT_TEXT = 'Stop promotions';

function placeholderCount(text: string): number {
  let highest = 0;
  for (const m of text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) highest = Math.max(highest, Number(m[1]));
  return highest;
}

export function TemplateEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const from = params.get('from');
  const editing = id !== undefined;
  const sourceId = editing ? Number(id) : from ? Number(from) : null;

  const source = useQuery({
    queryKey: ['template', sourceId],
    queryFn: () => inboxApi.template(sourceId!),
    enabled: sourceId !== null,
  });

  if (sourceId !== null && source.isPending) {
    return <div className="grid h-full place-items-center text-small text-(--color-muted)">Loading…</div>;
  }
  if (sourceId !== null && (source.isError || !source.data)) {
    return (
      <div className="grid h-full place-items-center text-center">
        <p className="text-body text-(--color-muted)">That template could not be opened. <Link to="/templates" className="text-(--color-brand) hover:underline">Back to templates</Link></p>
      </div>
    );
  }

  const initial = source.data
    ? (editing
        ? templateToDraft(source.data)
        : { ...templateToDraft(source.data), name: `${source.data.name}_copy`.slice(0, 512), headerMediaAssetId: null })
    : BLANK;

  return <Editor key={sourceId ?? 'new'} initial={initial} editing={editing} templateId={editing ? Number(id) : null} />;
}

function Editor({ initial, editing, templateId }: { initial: TemplateDraft; editing: boolean; templateId: number | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<TemplateDraft>(initial);
  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState(false);
  const [sample, setSample] = useState<{ name: string; url: string | null } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [serverError, setServerError] = useState<{ text: string; existingId: number | null } | null>(null);
  // Set the instant the submit is clicked, before React has re-rendered with
  // isPending: a second click in that gap is what created a duplicate at Meta.
  const inFlight = useRef(false);

  const auth = isAuthentication(draft);
  const bodyVariables = useMemo(() => placeholderCount(draft.body), [draft.body]);
  const headerVariables = draft.headerFormat === 'TEXT' ? placeholderCount(draft.headerText ?? '') : 0;
  const urlVariables = draft.buttons.filter((b) => b.type === 'URL' && placeholderCount(b.url ?? '') > 0).length;
  const hasVariables = !auth && (bodyVariables > 0 || headerVariables > 0 || urlVariables > 0);

  // Authentication has nothing to put in Buttons or Variables — Meta writes both.
  const steps = auth ? ['Basic Info', 'Content'] : hasVariables ? ['Basic Info', 'Content', 'Buttons', 'Variables'] : ['Basic Info', 'Content', 'Buttons'];
  const last = step === steps.length - 1;
  useEffect(() => { if (step > steps.length - 1) setStep(steps.length - 1); }, [step, steps.length]);

  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels });
  const fields = useQuery({ queryKey: ['contact-fields'], queryFn: inboxApi.contactFields, enabled: !auth });

  const set = <K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const problems = useMemo(() => stepProblems(draft, step, steps), [draft, step, steps]);

  const submit = useMutation({
    mutationFn: () => (editing && templateId !== null ? inboxApi.editTemplate(templateId, draft) : inboxApi.createTemplate(draft)),
    onSuccess: ({ templateId: savedId }) => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      void queryClient.invalidateQueries({ queryKey: ['template', savedId] });
      navigate(`/templates/${savedId}`, { replace: true });
    },
    onError: (e) => {
      const existingId = e instanceof ApiError && e.code === 'already_exists' && typeof e.body?.templateId === 'number'
        ? e.body.templateId
        : null;
      setServerError({ text: e instanceof Error ? e.message : 'WhatsApp did not accept the template.', existingId });
    },
    onSettled: () => { inFlight.current = false; },
  });

  const next = () => {
    setTouched(true);
    if (problems.length > 0) return;
    setTouched(false);
    if (!last) { setStep((n) => n + 1); return; }
    if (inFlight.current) return;
    inFlight.current = true;
    setServerError(null);
    submit.mutate();
  };
  const goTo = (n: number) => {
    if (n <= step) { setStep(n); setTouched(false); return; }
    setTouched(true);
    if (problems.length === 0 && n === step + 1) { setStep(n); setTouched(false); }
  };

  const shape = shapeOfDraft(draft, sample?.url);
  const activeChannels = (channels.data ?? []).filter((c) => c.isActive);

  return (
    <div className="flex h-full min-h-0 flex-col bg-(--color-surface)">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-(--color-line) px-3 sm:px-4">
        <Link to={templateId !== null ? `/templates/${templateId}` : '/templates'} aria-label="Back to templates" className="tap grid size-9 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken)">
          <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M16 10H4M9 4.5 3.5 10 9 15.5" {...s} strokeWidth={1.8} /></svg>
        </Link>
        <h1 className="truncate text-lead font-semibold">{editing ? 'Edit template' : 'Create new template'}</h1>
        <button type="button" onClick={() => setPreviewOpen(true)} className="tap ml-auto flex h-9 items-center gap-1.5 rounded-(--radius-field) border border-(--color-line) px-3 text-body font-medium text-(--color-ink-soft) hover:bg-(--color-sunken) lg:hidden">
          <IconPhoneSmall /> Preview
        </button>
      </header>

      <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_23.75rem]">
        <div className="flex min-h-0 flex-col">
          <nav aria-label="Steps" className="flex shrink-0 border-b border-(--color-line)">
            {steps.map((label, n) => {
              const active = n === step;
              const done = n < step;
              return (
                <button
                  key={label} type="button" onClick={() => goTo(n)} aria-current={active ? 'step' : undefined}
                  className={`tap flex h-12 flex-1 items-center justify-center gap-2 border-b-2 text-body ${active ? 'border-(--color-brand) font-semibold text-(--color-ink)' : 'border-transparent font-medium text-(--color-muted)'}`}
                >
                  <span className={`grid size-5 place-items-center rounded-full text-micro font-semibold ${active || done ? 'bg-(--color-brand) text-(--color-brand-ink)' : 'bg-(--color-line) text-(--color-surface)'}`}>{n + 1}</span>
                  <span className="hidden sm:inline">{label}</span>
                </button>
              );
            })}
          </nav>

          <div className="pane-scroll min-h-0 flex-1 overflow-y-auto bg-(--color-paper) p-3 sm:p-5">
            <div className="mx-auto max-w-5xl">
              {steps[step] === 'Basic Info' && (
                <BasicInfo draft={draft} set={set} editing={editing} channels={activeChannels} />
              )}
              {steps[step] === 'Content' && (auth
                ? <AuthContent draft={draft} set={set} />
                : <Content draft={draft} set={set} sample={sample} setSample={setSample} fields={(fields.data ?? []).map((f) => f.label)} />)}
              {steps[step] === 'Buttons' && <Buttons draft={draft} set={set} />}
              {steps[step] === 'Variables' && <Variables draft={draft} set={set} />}

              {touched && problems.length > 0 && (
                <ul role="alert" className="mt-4 rounded-(--radius-field) border border-(--color-bad)/40 bg-(--color-bad)/8 px-3 py-2 text-small text-(--color-bad)">
                  {problems.map((p) => <li key={p}>{p}</li>)}
                </ul>
              )}
              {serverError && (
                <p role="alert" className="mt-4 rounded-(--radius-field) border border-(--color-bad)/40 bg-(--color-bad)/8 px-3 py-2 text-small text-(--color-bad)">
                  {serverError.text}
                  {serverError.existingId !== null && (
                    <> <Link to={`/templates/${serverError.existingId}`} className="font-semibold underline">Open it</Link></>
                  )}
                </p>
              )}

              <div className={`mt-5 flex ${steps[step] === 'Buttons' ? 'justify-start' : 'justify-end'}`}>
                <button
                  type="button" onClick={next} disabled={submit.isPending}
                  className="tap flex h-10 items-center gap-2 rounded-(--radius-field) bg-(--color-brand) px-5 text-body font-semibold text-(--color-brand-ink) shadow-(--shadow-pop) hover:bg-(--color-brand-strong) disabled:opacity-60"
                >
                  {last ? (submit.isPending ? 'Submitting…' : editing ? 'Submit changes' : 'Submit Template') : <>Next <IconArrow /></>}
                </button>
              </div>
              {last && (
                <p className="mt-3 text-small text-(--color-muted)">
                  WhatsApp reviews every template before it can be sent; most answers arrive within a few hours. A marketing template that customers report costs the account's quality rating, so keep it to what they signed up for.
                </p>
              )}
            </div>
          </div>
        </div>

        <aside className="hidden min-h-0 flex-col border-l border-(--color-line) lg:flex">
          <h2 className="flex h-12 shrink-0 items-center border-b border-(--color-line) px-4 text-lead font-semibold">Message preview</h2>
          <div className="pane-scroll min-h-0 flex-1 overflow-y-auto p-4 pt-5">
            <PhonePreview shape={shape} />
          </div>
        </aside>
      </div>

      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 lg:hidden" onClick={() => setPreviewOpen(false)}>
          <div role="dialog" aria-label="Message preview" onClick={(e) => e.stopPropagation()} className="max-h-[90vh] w-full overflow-y-auto rounded-t-(--radius-panel) bg-(--color-surface) p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lead font-semibold">Message preview</h2>
              <button type="button" onClick={() => setPreviewOpen(false)} aria-label="Close" className="tap grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
                <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg>
              </button>
            </div>
            <PhonePreview shape={shape} />
          </div>
        </div>
      )}
    </div>
  );
}

/** What each step refuses to leave with. Mirrors the server's validator for the part on screen. */
function stepProblems(draft: TemplateDraft, step: number, steps: string[]): string[] {
  const out: string[] = [];
  const name = steps[step];
  const auth = isAuthentication(draft);
  if (name === 'Basic Info') {
    if (!draft.name.trim()) out.push('Give the template a name.');
    else if (!/^[a-z0-9_]+$/.test(draft.name)) out.push('Only lower case letters, numbers and underscores are allowed in the name.');
    if (!draft.language) out.push('Choose a language.');
  }
  if (name === 'Content' && !auth) {
    if (draft.headerFormat === 'TEXT' && !(draft.headerText ?? '').trim()) out.push('A text header needs some text.');
    if (draft.headerFormat === 'TEXT' && placeholderCount(draft.headerText ?? '') > 1) out.push('A header may contain at most one value, {{1}}.');
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(draft.headerFormat) && !draft.headerMediaAssetId) out.push(`Upload a sample ${draft.headerFormat.toLowerCase()} for WhatsApp's reviewer.`);
    if (!draft.body.trim()) out.push('Type the message body.');
    const highest = placeholderCount(draft.body);
    for (let i = 1; i <= highest; i += 1) if (!draft.body.includes(`{{${i}}}`)) out.push(`The body uses {{${highest}}} but never {{${i}}}. Numbering must run from 1 with no gaps.`);
    if (/\}\}\s*\{\{/.test(draft.body)) out.push('Two values cannot sit next to each other; put some text between them.');
    if (/^\s*\{\{\d+\}\}/.test(draft.body) || /\{\{\d+\}\}\s*$/.test(draft.body)) out.push('The body cannot begin or end with a value.');
    if (/\{\{\s*\d+\s*\}\}/.test(draft.footer ?? '')) out.push('A footer cannot contain values.');
  }
  if (name === 'Content' && auth) {
    const m = draft.codeExpirationMinutes;
    if (m !== null && m !== undefined && (m < 1 || m > 90)) out.push('The code can expire in 1 to 90 minutes.');
    if (draft.otpType === 'ONE_TAP' && !draft.otpPackageName?.trim()) out.push("A one-tap button needs the Android app's package name.");
    if (draft.otpType === 'ONE_TAP' && !draft.otpSignatureHash?.trim()) out.push("A one-tap button needs the app's signature hash.");
  }
  if (name === 'Buttons') {
    draft.buttons.forEach((b, i) => {
      if (b.type !== 'COPY_CODE' && !(b.text ?? '').trim()) out.push(`Button ${i + 1} needs a label.`);
      if (b.type === 'URL' && !/^https?:\/\/\S+/.test(b.url ?? '')) out.push(`Button ${i + 1} needs a full web address, starting with https://.`);
      if (b.type === 'PHONE_NUMBER' && !/^\+?\d{8,15}$/.test((b.phoneNumber ?? '').replace(/[\s-]/g, ''))) out.push(`Button ${i + 1} needs a phone number with the country code.`);
      if (b.type === 'COPY_CODE' && !(b.example ?? '').trim()) out.push(`Button ${i + 1} needs a sample code.`);
    });
    if (draft.buttons.filter((b) => b.type === 'URL').length > 2) out.push('WhatsApp allows two website buttons.');
    if (draft.buttons.filter((b) => b.type === 'PHONE_NUMBER').length > 1) out.push('WhatsApp allows one call button.');
    if (draft.buttons.filter((b) => b.type === 'COPY_CODE').length > 1) out.push('WhatsApp allows one copy-code button.');
  }
  if (name === 'Variables') {
    const highest = placeholderCount(draft.body);
    for (let i = 0; i < highest; i += 1) if (!(draft.bodyExamples[i] ?? '').trim()) out.push(`Give a sample value for {{${i + 1}}} in the body.`);
    if (draft.headerFormat === 'TEXT' && placeholderCount(draft.headerText ?? '') > 0 && !(draft.headerExample ?? '').trim()) out.push('Give a sample value for {{1}} in the header.');
    draft.buttons.forEach((b, i) => { if (b.type === 'URL' && placeholderCount(b.url ?? '') > 0 && !(b.example ?? '').trim()) out.push(`Give a sample for the value in button ${i + 1}'s address.`); });
  }
  return out;
}

// ------------------------------------------------------------------ step 1 --

function BasicInfo({ draft, set, editing, channels }: {
  draft: TemplateDraft; set: Setter; editing: boolean; channels: { verifiedName: string | null; displayPhoneNumber: string }[];
}) {
  function setCategory(category: TemplateDraft['category']) {
    if (category === draft.category) return;
    if (category === 'AUTHENTICATION') {
      set('category', category);
      set('headerFormat', 'NONE'); set('headerText', undefined); set('headerExample', undefined); set('headerMediaAssetId', null);
      set('body', ''); set('bodyExamples', []); set('footer', undefined); set('buttons', []);
      set('otpType', draft.otpType ?? 'COPY_CODE'); set('addSecurityRecommendation', draft.addSecurityRecommendation ?? true);
      set('codeExpirationMinutes', draft.codeExpirationMinutes ?? 10);
      return;
    }
    set('category', category);
    set('addSecurityRecommendation', undefined); set('codeExpirationMinutes', undefined); set('otpType', undefined);
    set('otpButtonText', undefined); set('otpPackageName', undefined); set('otpSignatureHash', undefined); set('otpAutofillText', undefined);
  }
  return (
    <div className="flex flex-col gap-4">
      <Card title="Template name" hint="Enter template name">
        <Outlined label="Type template name" count={[draft.name.length, 512]}>
          <input
            value={draft.name} disabled={editing} autoFocus={!editing}
            onChange={(e) => set('name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
            className={field} aria-label="Template name"
          />
        </Outlined>
        <p className="mt-1.5 text-micro text-(--color-muted)">
          {editing ? 'WhatsApp does not allow a template’s name to change. Duplicate it for a new name.' : 'Only lower case letters(a), numbers(0) and underscore (_) allowed E.g.: stock1_clearance_sale'}
        </p>
      </Card>

      <Card title="Category" hint="Choose a category that describes your message template.">
        <div className="flex flex-col gap-3">
          {CATEGORIES.map((c) => {
            const on = draft.category === c.value;
            return (
              <label key={c.value} className={`tap flex cursor-pointer items-start gap-3 rounded-(--radius-field) border p-3 sm:p-4 ${on ? 'border-(--color-brand) bg-(--color-brand-wash)' : 'border-(--color-line) hover:border-(--color-muted)'}`}>
                <input type="radio" name="category" className="sr-only" checked={on} disabled={editing} onChange={() => setCategory(c.value)} />
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-(--color-sunken) text-(--color-ink-soft)">{c.icon}</span>
                <span className="min-w-0">
                  <span className="block text-lead font-semibold">{c.label}</span>
                  <span className="mt-0.5 block text-body text-(--color-ink-soft)">{c.hint}</span>
                  {c.value === 'AUTHENTICATION' && on && (
                    <span className="mt-1 block text-small text-(--color-muted)">Needs a verified business on WhatsApp — an unverified account cannot create one.</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
        {editing && <p className="mt-2 text-micro text-(--color-muted)">The category is WhatsApp's to decide once submitted; it can re-categorise a template it disagrees with.</p>}
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card title="Language" hint="Choose language for this template">
          <Outlined label="Language">
            <select value={draft.language} disabled={editing} onChange={(e) => set('language', e.target.value)} className={`${field} appearance-none pr-8`} aria-label="Language">
              {TEMPLATE_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <IconChevronAbs />
          </Outlined>
        </Card>
        <Card title="Channels" hint="The template is created on your WhatsApp Business Account and works from every number on it">
          <div className="flex min-h-11 flex-wrap items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) px-3 py-2">
            {channels.length === 0 && <span className="text-body text-(--color-muted)">No active WhatsApp number yet</span>}
            {channels.map((c) => (
              <span key={c.displayPhoneNumber} className="inline-flex items-center gap-1.5 rounded-(--radius-chip) bg-(--color-surface) px-2 py-0.5 text-small font-medium text-(--color-ink-soft)">
                <IconWhatsApp /> {c.verifiedName ?? c.displayPhoneNumber}
              </span>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ step 2 --

function Content({ draft, set, sample, setSample, fields }: {
  draft: TemplateDraft; set: Setter; sample: { name: string; url: string | null } | null;
  setSample: (s: { name: string; url: string | null } | null) => void; fields: string[];
}) {
  const bodyBox = useRef<HTMLTextAreaElement>(null);
  const bodyVariables = placeholderCount(draft.body);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) => inboxApi.uploadTemplateSample(file),
    onSuccess: (r, file) => {
      set('headerMediaAssetId', r.mediaAssetId);
      setSample({ name: r.fileName, url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null });
      setUploadError(null);
    },
    onError: (e) => setUploadError(e instanceof Error ? e.message : 'The file could not be uploaded.'),
  });

  // One setter for the body, wherever the change comes from — typing, a
  // variable chip, the formatting toolbar — so the samples always follow the
  // {{n}} count; the server refuses a draft where they differ.
  function setBody(body: string) {
    set('body', body);
    const n = placeholderCount(body);
    set('bodyExamples', Array.from({ length: n }, (_, i) => draft.bodyExamples[i] ?? ''));
  }

  function insertVariable() {
    const n = bodyVariables + 1;
    const token = `{{${n}}}`;
    const box = bodyBox.current;
    const at = box?.selectionStart ?? draft.body.length;
    const end = box?.selectionEnd ?? at;
    set('body', draft.body.slice(0, at) + token + draft.body.slice(end));
    set('bodyExamples', [...draft.bodyExamples.slice(0, bodyVariables), '']);
    queueMicrotask(() => { box?.focus(); box?.setSelectionRange(at + token.length, at + token.length); });
  }

  const chips = ['Name', 'Phone number', ...fields].slice(0, 8);

  return (
    <div className="flex flex-col gap-4">
      <Card title="Header (Optional)" hint="Add a title for your message. Your title can't include more than one variable">
        <div className="flex flex-wrap gap-1.5">
          {HEADERS.map((f) => (
            <button key={f} type="button" onClick={() => { set('headerFormat', f); if (f !== 'TEXT') { set('headerText', undefined); set('headerExample', undefined); } if (['NONE', 'TEXT'].includes(f)) { set('headerMediaAssetId', null); setSample(null); } }}
              className={`tap h-9 rounded-(--radius-field) border px-3 text-body font-medium ${draft.headerFormat === f ? 'border-(--color-brand) bg-(--color-brand) text-(--color-brand-ink)' : 'border-(--color-line) text-(--color-ink-soft) hover:bg-(--color-sunken)'}`}>
              {f === 'NONE' ? 'None' : f[0] + f.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        {draft.headerFormat === 'TEXT' && (
          <div className="mt-3">
            <Outlined label="Type message title" count={[draft.headerText?.length ?? 0, TEMPLATE_LIMITS.header]}>
              <input value={draft.headerText ?? ''} maxLength={TEMPLATE_LIMITS.header} onChange={(e) => set('headerText', e.target.value)} className={field} aria-label="Header text" />
            </Outlined>
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <p className="text-micro text-(--color-muted)">One value at most, written {'{{1}}'}.</p>
              {placeholderCount(draft.headerText ?? '') === 0 && (
                <button type="button" onClick={() => set('headerText', `${draft.headerText ?? ''}{{1}}`)} className="tap text-small font-medium text-(--color-ink-soft) hover:text-(--color-ink)">+ Add Variable</button>
              )}
            </div>
          </div>
        )}
        {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(draft.headerFormat) && (
          <div className="mt-3">
            <p className="text-small text-(--color-ink-soft)">
              {draft.headerFormat === 'IMAGE' && 'Image type allowed: JPG, JPEG, PNG'}
              {draft.headerFormat === 'VIDEO' && 'Video type allowed: MP4'}
              {draft.headerFormat === 'DOCUMENT' && 'Document type allowed: PDF'}
            </p>
            <p className="mt-0.5 text-small text-(--color-ink-soft)">File size limit: 16 MB. It is a sample for WhatsApp's reviewer; the file sent to customers is chosen at send time.</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <label className="tap inline-flex h-10 cursor-pointer items-center rounded-(--radius-field) bg-(--color-brand) px-4 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong)">
                {upload.isPending ? 'Uploading…' : sample ? 'Replace file' : 'Upload a file'}
                <input type="file" accept={ACCEPT[draft.headerFormat]} className="sr-only" disabled={upload.isPending} onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
              </label>
              {sample && <span className="inline-flex items-center gap-1.5 text-body text-(--color-ink-soft)"><IconFile /> {sample.name}</span>}
              {!sample && draft.headerMediaAssetId && <span className="text-body text-(--color-muted)">A sample is already attached.</span>}
            </div>
            {uploadError && <p role="alert" className="mt-2 text-small text-(--color-bad)">{uploadError}</p>}
          </div>
        )}
      </Card>

      <Card title="Body" titleAside={<span title="Values are numbered {{1}}, {{2}}… and filled from contact fields when you send." className="text-(--color-muted)"><IconInfo /></span>}>
        <div className="flex flex-wrap gap-1.5">
          {chips.map((label) => (
            <button key={label} type="button" onClick={insertVariable} disabled={bodyVariables >= 20}
              className="tap rounded-(--radius-field) border border-(--color-line) px-2.5 py-1 text-small font-medium text-(--color-ink-soft) hover:bg-(--color-sunken) disabled:opacity-50">
              {label}
            </button>
          ))}
        </div>
        <div className="relative mt-3 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) focus-within:border-(--color-brand)">
          {/* WhatsApp's own markers — *bold*, _italic_, ~strike~, ```mono``` — and
              an emoji picker. The body stays a plain string; the phone on the
              right draws the markers the way a customer's phone will. */}
          <FormatToolbar box={bodyBox} value={draft.body} onChange={setBody} className="border-b border-(--color-line-dim) px-1.5 py-1" />
          <textarea
            ref={bodyBox} rows={5} value={draft.body} maxLength={TEMPLATE_LIMITS.body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type message body" aria-label="Message body"
            className={`${field} min-h-28 resize-y leading-relaxed`}
          />
          <span className="pointer-events-none absolute right-2.5 bottom-2 text-micro text-(--color-faint)">({draft.body.length}/{TEMPLATE_LIMITS.body})</span>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-3">
          <p className="text-micro text-(--color-muted)">Values are numbered, e.g. {'{{1}}'}; a chip inserts the next one. Each is mapped to a contact field when you send.</p>
          <button type="button" onClick={insertVariable} className="tap shrink-0 text-small font-medium text-(--color-ink-soft) hover:text-(--color-ink)">+ Add Variable</button>
        </div>
      </Card>

      <Card title="Footer (Optional)">
        <Outlined label="Type message footer" count={[draft.footer?.length ?? 0, TEMPLATE_LIMITS.footer]}>
          <input value={draft.footer ?? ''} maxLength={TEMPLATE_LIMITS.footer} onChange={(e) => set('footer', e.target.value || undefined)} className={field} aria-label="Footer" />
        </Outlined>
      </Card>
    </div>
  );
}

function AuthContent({ draft, set }: { draft: TemplateDraft; set: Setter }) {
  const oneTap = draft.otpType === 'ONE_TAP';
  return (
    <div className="flex flex-col gap-4">
      <Card title="Message" hint="WhatsApp writes and translates the message for an authentication template, so there is nothing to type. The preview shows what it will say.">
        <label className="flex cursor-pointer items-start gap-2.5">
          <input type="checkbox" checked={draft.addSecurityRecommendation ?? false} onChange={(e) => set('addSecurityRecommendation', e.target.checked)} className="mt-0.5 size-4 shrink-0 accent-(--color-brand)" />
          <span><span className="block text-body">Add security recommendation</span><span className="block text-small text-(--color-muted)">Appends "For your security, do not share this code."</span></span>
        </label>
        <div className="mt-3 max-w-60">
          <Outlined label="Code expires after (minutes)">
            <input type="number" min={1} max={90} value={draft.codeExpirationMinutes ?? ''} onChange={(e) => set('codeExpirationMinutes', e.target.value === '' ? null : Number(e.target.value))} className={field} aria-label="Code expiry in minutes" />
          </Outlined>
          <p className="mt-1 text-micro text-(--color-muted)">1 to 90. Leave blank for no expiry line.</p>
        </div>
      </Card>
      <Card title="Button" hint="The customer taps to copy the code, or WhatsApp hands it straight to your Android app.">
        <div className="flex flex-wrap gap-1.5">
          {(['COPY_CODE', 'ONE_TAP'] as const).map((t) => (
            <button key={t} type="button" onClick={() => set('otpType', t)} className={`tap h-9 rounded-(--radius-field) border px-3 text-body font-medium ${(draft.otpType ?? 'COPY_CODE') === t ? 'border-(--color-brand) bg-(--color-brand) text-(--color-brand-ink)' : 'border-(--color-line) text-(--color-ink-soft) hover:bg-(--color-sunken)'}`}>
              {t === 'COPY_CODE' ? 'Copy code' : 'One-tap autofill'}
            </button>
          ))}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Outlined label="Button label (optional)" count={[draft.otpButtonText?.length ?? 0, TEMPLATE_LIMITS.buttonText]}>
            <input value={draft.otpButtonText ?? ''} maxLength={TEMPLATE_LIMITS.buttonText} onChange={(e) => set('otpButtonText', e.target.value)} className={field} aria-label="Button label" />
          </Outlined>
          {oneTap && (
            <>
              <Outlined label="Android package name"><input value={draft.otpPackageName ?? ''} onChange={(e) => set('otpPackageName', e.target.value)} className={field} aria-label="Package name" /></Outlined>
              <Outlined label="Signature hash"><input value={draft.otpSignatureHash ?? ''} onChange={(e) => set('otpSignatureHash', e.target.value)} className={field} aria-label="Signature hash" /></Outlined>
              <Outlined label="Autofill label (optional)"><input value={draft.otpAutofillText ?? ''} maxLength={TEMPLATE_LIMITS.buttonText} onChange={(e) => set('otpAutofillText', e.target.value)} className={field} aria-label="Autofill label" /></Outlined>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ step 3 --

const BUTTON_KINDS: { group: string; items: { key: string; label: string; note?: string; make: () => TemplateDraftButton }[] }[] = [
  { group: 'Quick reply', items: [
    { key: 'optout', label: 'Marketing opt-out', note: 'Recommended', make: () => ({ type: 'QUICK_REPLY', text: OPT_OUT_TEXT }) },
    { key: 'custom', label: 'Custom button', make: () => ({ type: 'QUICK_REPLY', text: '' }) },
  ] },
  { group: 'Call to action', items: [
    { key: 'url', label: 'Visit website', note: '2 buttons maximum', make: () => ({ type: 'URL', text: '', url: 'https://' }) },
    { key: 'phone', label: 'Call phone number', note: '1 button maximum', make: () => ({ type: 'PHONE_NUMBER', text: '', phoneNumber: '+91' }) },
    { key: 'code', label: 'Copy offer code', note: '1 button maximum', make: () => ({ type: 'COPY_CODE', example: '' }) },
  ] },
];

function Buttons({ draft, set }: { draft: TemplateDraft; set: Setter }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const buttons = draft.buttons;
  const setButton = (i: number, patch: Partial<TemplateDraftButton>) => set('buttons', buttons.map((b, n) => (n === i ? { ...b, ...patch } : b)));
  const remove = (i: number) => set('buttons', buttons.filter((_, n) => n !== i));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d; if (j < 0 || j >= buttons.length) return;
    const next = [...buttons]; [next[i], next[j]] = [next[j]!, next[i]!]; set('buttons', next);
  };
  const add = (b: TemplateDraftButton) => { set('buttons', [...buttons, b]); setOpen(false); };
  const count = (t: TemplateButtonType) => buttons.filter((b) => b.type === t).length;
  const disabled = (key: string) =>
    buttons.length >= 10
    || (key === 'optout' && buttons.some((b) => b.text === OPT_OUT_TEXT))
    || (key === 'url' && count('URL') >= 2) || (key === 'phone' && count('PHONE_NUMBER') >= 1) || (key === 'code' && count('COPY_CODE') >= 1);
  const hasOptOut = buttons.some((b) => b.type === 'QUICK_REPLY' && b.text === OPT_OUT_TEXT);

  return (
    <div>
      {draft.category === 'MARKETING' && !hasOptOut && (
        <div className="mb-4 flex items-start gap-3 rounded-(--radius-field) border border-(--color-note-line) bg-(--color-note) px-4 py-3">
          <span className="mt-0.5 text-(--color-note-strong)"><IconInfo /></span>
          <div className="min-w-0 flex-1">
            <p className="text-body font-semibold text-(--color-note-ink)">Give customers a way out of marketing messages</p>
            <p className="mt-0.5 text-small text-(--color-note-ink)">A "Stop promotions" button is what stops a report to WhatsApp — and a report is what costs the account its quality rating. This platform honours the reply automatically.</p>
          </div>
        </div>
      )}
      <h2 className="text-lead font-semibold">Buttons</h2>
      <p className="mt-0.5 text-body text-(--color-ink-soft)">Create buttons that let customers respond to your message or take action</p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div ref={box} className="relative">
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="true" disabled={buttons.length >= 10}
            className="tap flex h-10 items-center gap-1.5 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-3 text-body font-medium text-(--color-ink) hover:bg-(--color-sunken) disabled:opacity-50">
            <IconPlus /> Add a button <svg viewBox="0 0 20 20" className={`size-4 text-(--color-muted) transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true"><path d="m6 8 4 4 4-4" {...s} /></svg>
          </button>
          {open && (
            <div className="absolute top-full left-0 z-20 mt-1 w-56 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)">
              {BUTTON_KINDS.map((g) => (
                <div key={g.group} className="border-b border-(--color-line-dim) py-1 last:border-b-0">
                  <p className="px-3 py-1 text-micro font-semibold tracking-wide text-(--color-muted) uppercase">{g.group}</p>
                  {g.items.map((it) => (
                    <button key={it.key} type="button" disabled={disabled(it.key)} onClick={() => add(it.make())}
                      className="tap block w-full px-3 py-1.5 text-left hover:bg-(--color-sunken) disabled:opacity-40">
                      <span className="block text-body">{it.label}</span>
                      {it.note && <span className={`block text-micro ${it.note === 'Recommended' ? 'text-(--color-brand)' : 'text-(--color-muted)'}`}>{it.note}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <span className="text-body text-(--color-ink-soft)">{buttons.length}/10 buttons</span>
      </div>
      <p className="mt-1.5 text-micro text-(--color-muted)">If you add more than 3 buttons, they will appear in a list</p>

      {buttons.length === 0 && <p className="mt-4 text-body">No buttons</p>}

      <div className="mt-4 flex flex-col gap-3">
        {buttons.map((b, i) => (
          <div key={i} className="rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <span className="flex flex-col text-(--color-muted)">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="tap disabled:opacity-30"><svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="m6 12 4-4 4 4" {...s} /></svg></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === buttons.length - 1} aria-label="Move down" className="tap disabled:opacity-30"><svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="m6 8 4 4 4-4" {...s} /></svg></button>
              </span>
              <span className="text-body font-semibold">{b.type === 'QUICK_REPLY' ? 'Quick Reply' : b.type === 'COPY_CODE' ? 'Copy code' : 'Call to action'}</span>
              <button type="button" onClick={() => remove(i)} aria-label={`Remove button ${i + 1}`} className="tap ml-auto grid size-8 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-bad)">
                <svg viewBox="0 0 20 20" className="size-4.5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg>
              </button>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
              {b.type !== 'QUICK_REPLY' && b.type !== 'COPY_CODE' && (
                <Outlined label="Type of action">
                  <select value={b.type} onChange={(e) => setButton(i, { type: e.target.value as TemplateButtonType })} className={`${field} appearance-none pr-8`} aria-label="Type of action">
                    <option value="URL">Visit website</option>
                    <option value="PHONE_NUMBER">Call phone number</option>
                  </select>
                  <IconChevronAbs />
                </Outlined>
              )}
              {b.type !== 'COPY_CODE' && (
                <Outlined label="Button name" count={[b.text?.length ?? 0, TEMPLATE_LIMITS.buttonText]} className={b.type === 'QUICK_REPLY' ? 'sm:col-span-2' : ''}>
                  <input value={b.text ?? ''} maxLength={TEMPLATE_LIMITS.buttonText} onChange={(e) => setButton(i, { text: e.target.value })} className={field} aria-label={`Button ${i + 1} name`} />
                </Outlined>
              )}
              {b.type === 'URL' && (
                <Outlined label="Website URL" count={[b.url?.length ?? 0, TEMPLATE_LIMITS.url]} className="sm:col-span-2">
                  <input value={b.url ?? ''} onChange={(e) => setButton(i, { url: e.target.value })} placeholder="https://example.com/track/{{1}}" className={field} aria-label={`Button ${i + 1} address`} />
                </Outlined>
              )}
              {b.type === 'PHONE_NUMBER' && (
                <Outlined label="Phone number" className="sm:col-span-2">
                  <input value={b.phoneNumber ?? ''} onChange={(e) => setButton(i, { phoneNumber: e.target.value })} placeholder="+91 98765 43210" className={field} aria-label={`Button ${i + 1} phone number`} />
                </Outlined>
              )}
              {b.type === 'COPY_CODE' && (
                <Outlined label="Sample offer code" count={[b.example?.length ?? 0, TEMPLATE_LIMITS.copyCode]} className="sm:col-span-2">
                  <input value={b.example ?? ''} maxLength={TEMPLATE_LIMITS.copyCode} onChange={(e) => setButton(i, { example: e.target.value })} placeholder="DIWALI20" className={field} aria-label={`Button ${i + 1} sample code`} />
                </Outlined>
              )}
            </div>
            {b.type === 'URL' && (
              <p className="mt-2 text-micro text-(--color-muted)">The address may end in one value, {'{{1}}'}, filled per customer at send time.</p>
            )}
          </div>
        ))}
      </div>

      <label className="mt-4 flex cursor-pointer items-center gap-2.5">
        <input type="checkbox" checked={hasOptOut} onChange={(e) => set('buttons', e.target.checked ? [...buttons, { type: 'QUICK_REPLY', text: OPT_OUT_TEXT }] : buttons.filter((b) => !(b.type === 'QUICK_REPLY' && b.text === OPT_OUT_TEXT)))} disabled={!hasOptOut && buttons.length >= 10} className="size-4 accent-(--color-brand)" />
        <span className="text-body">Add stop button</span>
        <span className="text-small text-(--color-muted)">— a "{OPT_OUT_TEXT}" reply opts the customer out here automatically</span>
      </label>
    </div>
  );
}

// ------------------------------------------------------------------ step 4 --

function Variables({ draft, set }: { draft: TemplateDraft; set: Setter }) {
  const n = placeholderCount(draft.body);
  const samples = Array.from({ length: n }, (_, i) => draft.bodyExamples[i] ?? '');
  const headerHas = draft.headerFormat === 'TEXT' && placeholderCount(draft.headerText ?? '') > 0;
  const urlButtons = draft.buttons.map((b, i) => ({ b, i })).filter(({ b }) => b.type === 'URL' && placeholderCount(b.url ?? '') > 0);
  return (
    <div>
      <h2 className="text-lead font-semibold">Variables examples</h2>
      <p className="mt-1 text-small text-(--color-ink-soft)">
        To help WhatsApp review your template content, provide examples of the variables. Do not include any customer information.
        <br />The examples are for the reviewer only; real values come from each contact when you send.
      </p>
      {headerHas && (
        <Card title="Header" className="mt-4">
          <p className="mb-2 text-body font-semibold">{'{{1}}'}</p>
          <Outlined label="Type sample value"><input value={draft.headerExample ?? ''} onChange={(e) => set('headerExample', e.target.value)} className={field} aria-label="Sample for header value" /></Outlined>
        </Card>
      )}
      {n > 0 && (
        <Card title="Body" className="mt-4">
          <div className="flex flex-col gap-3">
            {samples.map((v, i) => (
              <div key={i}>
                <p className="mb-2 text-body font-semibold">{`{{${i + 1}}}`}</p>
                <Outlined label="Type sample value">
                  <input value={v} onChange={(e) => set('bodyExamples', samples.map((x, k) => (k === i ? e.target.value : x)))} className={field} aria-label={`Sample for value ${i + 1}`} />
                </Outlined>
              </div>
            ))}
          </div>
        </Card>
      )}
      {urlButtons.length > 0 && (
        <Card title="Buttons" className="mt-4">
          <div className="flex flex-col gap-3">
            {urlButtons.map(({ b, i }) => (
              <div key={i}>
                <p className="mb-2 text-body font-semibold">{b.text || `Button ${i + 1}`} — {'{{1}}'}</p>
                <Outlined label="Full sample address">
                  <input value={b.example ?? ''} onChange={(e) => set('buttons', draft.buttons.map((x, k) => (k === i ? { ...x, example: e.target.value } : x)))} placeholder={(b.url ?? '').replace(/\{\{1\}\}/, '4417')} className={field} aria-label={`Sample address for button ${i + 1}`} />
                </Outlined>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// -------------------------------------------------------------- primitives --

type Setter = <K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) => void;

// focus-visible:outline-none, not outline-none: the app's global focus ring is a
// :focus-visible rule of equal specificity that comes later, and the outlined
// label already draws the ring. Two rings read as a mistake.
const field = 'tap w-full min-w-0 bg-transparent px-3 py-2.5 text-body outline-none focus-visible:outline-none disabled:opacity-60';

function Card({ title, hint, titleAside, className = '', children }: {
  title: string; hint?: string; titleAside?: React.ReactNode; className?: string; children: React.ReactNode;
}) {
  return (
    <section className={`rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) p-4 ${className}`}>
      <h2 className="flex items-center gap-1.5 text-body font-semibold">{title}{titleAside}</h2>
      {hint && <p className="mt-0.5 text-small text-(--color-muted)">{hint}</p>}
      <div className={hint || title ? 'mt-3' : ''}>{children}</div>
    </section>
  );
}

/** The reference's outlined field: the label sits on the border, the count in the corner. */
function Outlined({ label, count, className = '', children }: {
  label: string; count?: [number, number]; className?: string; children: React.ReactNode;
}) {
  return (
    <label className={`relative block rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) focus-within:border-(--color-brand) ${className}`}>
      <span className="absolute -top-2 left-2.5 bg-(--color-surface) px-1 text-micro text-(--color-muted)">{label}</span>
      {children}
      {count && <span className={`pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-micro tabular-nums ${count[0] > count[1] * 0.9 ? 'text-(--color-bad)' : 'text-(--color-faint)'}`}>{count[0]}/{count[1]}</span>}
    </label>
  );
}

function IconChevronAbs() {
  return <svg viewBox="0 0 20 20" className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-(--color-muted)" aria-hidden="true"><path d="m6 8 4 4 4-4" {...s} /></svg>;
}
function IconMegaphone() { return <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M4 8.4v3.2a1 1 0 0 0 1 1h2.2L12 15.6V4.4L7.2 7.4H5a1 1 0 0 0-1 1Z" {...s} /><path d="M14.6 7.6a3.4 3.4 0 0 1 0 4.8" {...s} /></svg>; }
function IconBell() { return <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M5.5 13.5V9a4.5 4.5 0 0 1 9 0v4.5l1 1.5h-11zM8.5 16a1.5 1.5 0 0 0 3 0" {...s} /></svg>; }
function IconShield() { return <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="M10 3 4.5 5.2v4.6c0 3.4 2.3 5.8 5.5 7.2 3.2-1.4 5.5-3.8 5.5-7.2V5.2z" {...s} /><path d="m7.8 10 1.6 1.6 3-3.2" {...s} /></svg>; }
function IconWhatsApp() { return <svg viewBox="0 0 20 20" className="size-3.5 text-(--color-brand)" aria-hidden="true"><path d="M10 3a7 7 0 0 0-6 10.6L3 17l3.5-.9A7 7 0 1 0 10 3Z" {...s} /><path d="M7.6 7.4c.2-.4.5-.4.7-.4l.5.9-.5.7c.5 1 1.3 1.8 2.3 2.3l.7-.5.9.5c0 .2 0 .5-.4.7-.6.4-1.5.2-2.6-.7S7.2 8 7.6 7.4Z" fill="currentColor" /></svg>; }
function IconInfo() { return <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><circle cx="10" cy="10" r="7" {...s} /><path d="M10 9v4.5M10 6.5v.2" {...s} /></svg>; }
function IconPlus() { return <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M10 4v12M4 10h12" {...s} /></svg>; }
function IconArrow() { return <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5" {...s} /></svg>; }
function IconFile() { return <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M5 3h7l4 4v10H5z" {...s} /><path d="M12 3v4h4" {...s} /></svg>; }
function IconPhoneSmall() { return <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><rect x="6" y="2.5" width="8" height="15" rx="1.8" {...s} /><path d="M9 15h2" {...s} /></svg>; }
